// Global packages and components
import { clsx } from 'clsx'
import { useState } from 'react'
import { NavLink, Slider, Button, Image } from '~/components'

// Types
import type { SanityNavItemChild, SanityNavFeaturedItem } from '~/types'

interface Props {
  navItems: SanityNavItemChild[]
  featuredItems?: SanityNavFeaturedItem[]
  setMegaNavActive: (e: null) => void
}

const MegaNavInner = ({ navItems, featuredItems, setMegaNavActive }: Props) => {
  // Index of the hovered top-level item, starting at 1 (null shows no children)
  const [activeParent, setActiveParent] = useState<number | null>(null)

  return (
    <div className="grid h-[30rem] w-full grid-cols-3 overflow-hidden">
      <div className="flex min-h-full flex-col overflow-y-auto py-3">
        {navItems.map((parent, pi) => (
          <div
            className="px-3"
            key={'parent' + pi}
          >
            <NavLink
              {...parent}
              hasArrow
              hasIcon
              className={clsx('inline-block w-full rounded-lg p-3', {
                'bg-vibrant-green/10 font-semibold text-teal-green':
                  activeParent == pi + 1,
              })}
              onMouseEnter={() => setActiveParent(pi + 1)}
              onClick={() => setMegaNavActive(null)}
            />
          </div>
        ))}
      </div>
      <div className="min-h-full overflow-y-auto py-3">
        {navItems.map((parent, pi) => (
          <div
            key={'children' + pi}
            className={clsx({ hidden: activeParent !== pi + 1 })}
          >
            {parent.childItems?.map((child, ci) => (
              <div
                className="px-3"
                key={'child' + ci}
              >
                <NavLink
                  {...child}
                  hasArrow
                  className="inline-block w-full rounded-lg p-3 hover:font-semibold hover:text-teal-green"
                  onClick={() => setMegaNavActive(null)}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="relative h-full">
        {featuredItems && (
          <Slider
            options={{
              type: 'fade',
            }}
          >
            {featuredItems.map((item, i) => (
              <div
                className="relative h-full w-full"
                key={i}
              >
                {item?.title && (
                  <div className="absolute left-0 top-0 z-[1] flex h-full w-full flex-col justify-end bg-black/20 px-5 py-10">
                    <h4 className="mb-5 block text-xl font-bold text-white">
                      {item.title}
                    </h4>
                    {item?.slug && (
                      <div>
                        <Button
                          to={item?.slug}
                          title={item.linkText || 'Learn more'}
                          srOnlyText={
                            item.linkText ? undefined : `about ${item.title}`
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
                {item?.image?.asset && (
                  <Image
                    image={item.image}
                    customSize={768}
                    className="absolute left-0 top-0 h-full w-full object-cover"
                  />
                )}
              </div>
            ))}
          </Slider>
        )}
      </div>
    </div>
  )
}

export default MegaNavInner
